import { useEffect, useRef, useState } from "react";
import { FiEye, FiEyeOff, FiX } from "react-icons/fi";

export default function ResetPasswordModal({
  open,
  subjectName,
  loading = false,
  error = "",
  confirmLabel = "Reset Password",
  onCancel,
  onConfirm,
}) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);
  const loadingRef = useRef(loading);
  const onCancelRef = useRef(onCancel);
  loadingRef.current = loading;
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!open) return undefined;
    setPassword("");
    setShowPassword(false);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !loadingRef.current) {
        event.preventDefault();
        onCancelRef.current();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const trimmed = password.trim();
  const canSubmit = trimmed.length >= 6 && !loading;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onConfirm(trimmed);
  };

  const handleOverlayClick = () => {
    if (!loading) onCancel();
  };

  return (
    <div
      className="modal-overlay reset-password-overlay"
      role="presentation"
      onClick={handleOverlayClick}
    >
      <div
        className="modal-box reset-password-modal"
        role="dialog"
        aria-modal="true"
        aria-busy={loading}
        aria-labelledby="reset-password-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="reset-password-close"
          onClick={() => {
            if (!loading) onCancel();
          }}
          disabled={loading}
          aria-label="Close"
        >
          <FiX aria-hidden />
        </button>

        <h3 id="reset-password-title">Reset Password</h3>
        <p className="modal-subtitle">
          Enter a new password for{" "}
          <strong>{subjectName || "this account"}</strong>
        </p>

        <form onSubmit={handleSubmit}>
          <label className="modal-label" htmlFor="reset-password-input">
            New password
          </label>
          <div className="reset-password-input-wrap">
            <input
              id="reset-password-input"
              ref={inputRef}
              type={showPassword ? "text" : "password"}
              className="modal-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              autoComplete="new-password"
              minLength={6}
              disabled={loading}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "reset-password-error" : "reset-password-hint"}
            />
            <button
              type="button"
              className="reset-password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              disabled={loading}
            >
              {showPassword ? <FiEyeOff aria-hidden /> : <FiEye aria-hidden />}
            </button>
          </div>
          <p id="reset-password-hint" className="modal-hint">
            Minimum 6 characters
          </p>
          {error ? (
            <p id="reset-password-error" className="error-text">
              {error}
            </p>
          ) : null}
          <div className="modal-actions">
            <button
              type="button"
              className="secondary-page-btn"
              onClick={() => {
                if (!loading) onCancel();
              }}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="primary-page-btn" disabled={!canSubmit}>
              {loading ? "Resetting…" : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
